import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Input,
  FormControl,
  FormLabel,
  FormErrorMessage,
  FormHelperText,
  Text,
  VStack,
  HStack,
  Badge,
  Box,
  useToast,
  Spinner
} from '@chakra-ui/react';
import { useAuth } from '../context/AuthContext';

interface ScreenNameModalProps {
  isOpen: boolean;
  onClose: () => void;
  isEdit?: boolean;
  currentScreenName?: string;
}

export const ScreenNameModal: React.FC<ScreenNameModalProps> = ({
  isOpen,
  onClose,
  isEdit = false,
  currentScreenName = ''
}) => {
  const [screenName, setScreenName] = useState(currentScreenName);
  const [isChecking, setIsChecking] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationResult, setValidationResult] = useState<{
    available: boolean;
    valid: boolean;
    errors: string[];
    suggestions: string[];
    existing_count?: number;
    info_message?: string | null;
  } | null>(null);
  
  const { createScreenName, updateScreenName, checkScreenNameAvailability } = useAuth();
  const toast = useToast();

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setScreenName(currentScreenName);
      setValidationResult(null);
    }
  }, [isOpen, currentScreenName]);

  // Validate screen name on change (with debounce)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (screenName.length >= 2) {
        handleValidation();
      } else if (screenName.length > 0) {
        setValidationResult({
          available: false,
          valid: false,
          errors: ['Screen name must be at least 2 characters'],
          suggestions: []
        });
      } else {
        setValidationResult(null);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [screenName]);

  const handleValidation = async () => {
    if (screenName.length < 2) return;
    
    setIsChecking(true);
    try {
      const result = await checkScreenNameAvailability(screenName);
      setValidationResult(result);
    } catch (error) {
      console.error('Error validating screen name:', error);
    } finally {
      setIsChecking(false);
    }
  };

  const handleSubmit = async () => {
    if (!validationResult?.valid || !validationResult?.available) {
      toast({
        title: 'Invalid Screen Name',
        description: 'Please fix the validation errors before submitting',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsSubmitting(true);
    try {
      if (isEdit) {
        await updateScreenName(screenName);
      } else {
        await createScreenName(screenName);
      }
      onClose();
    } catch (error: any) {
      const collisionTag = error?.detail?.collision_tag || error?.detail?.detail?.collision_tag;
      if (collisionTag) {
        toast({
          title: 'Tag Collision',
          description: `${screenName}#${collisionTag} already exists. Please try again to generate a different tag.`,
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
      } else {
        // Error handling is done in the auth context
        console.error('Error submitting screen name:', error);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  const isValid = validationResult?.valid && validationResult?.available;
  const hasErrors = validationResult && (!validationResult.valid || !validationResult.available);
  const showSuggestions = validationResult?.suggestions && validationResult.suggestions.length > 0;

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={handleClose} 
      closeOnOverlayClick={!isSubmitting}
      size="md"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {isEdit ? 'Change Screen Name' : 'Create Your Screen Name'}
        </ModalHeader>
        <ModalCloseButton isDisabled={isSubmitting} />
        
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Text fontSize="sm" color="gray.600">
              {isEdit 
                ? 'Choose a new screen name for social features'
                : 'Your screen name is used for social features and is separate from character names'
              }
            </Text>
            
            <FormControl isInvalid={!!hasErrors} isRequired>
              <FormLabel>Screen Name</FormLabel>
              <Input
                value={screenName}
                onChange={(e) => setScreenName(e.target.value)}
                placeholder="Enter your screen name"
                maxLength={15}
                isDisabled={isSubmitting}
                autoFocus
              />
              
              <HStack mt={2} justify="space-between">
                <FormHelperText>
                  2-15 characters, letters, numbers, and spaces only
                </FormHelperText>
                <Text fontSize="xs" color="gray.500">
                  {screenName.length}/15
                </Text>
              </HStack>
              
              {isChecking && (
                <HStack mt={2}>
                  <Spinner size="sm" />
                  <Text fontSize="sm">Checking availability...</Text>
                </HStack>
              )}
              
              {validationResult && !isChecking && (
                <Box mt={2}>
                  {isValid && (
                    <VStack align="stretch" spacing={2}>
                      <HStack>
                        <Badge colorScheme="green" variant="subtle">Available</Badge>
                        <Text fontSize="sm" color="green.600">
                          ✓ Screen name is valid
                        </Text>
                      </HStack>
                      {validationResult.existing_count && validationResult.existing_count > 0 && (
                        <Text fontSize="sm" color="blue.400">
                          {validationResult.info_message ||
                            `${validationResult.existing_count} other user(s) have this name. A unique tag will be assigned.`}
                        </Text>
                      )}
                    </VStack>
                  )}
                  
                  {hasErrors && (
                    <VStack align="stretch" spacing={2}>
                      {validationResult.valid && !validationResult.available && (
                        <Text fontSize="sm" color="arcaneGold">
                          Create Screen Name is disabled because this screen name is already taken.
                        </Text>
                      )}
                      {validationResult.errors.map((error, index) => (
                        <FormErrorMessage key={index} mt={0}>
                          {error}
                        </FormErrorMessage>
                      ))}
                    </VStack>
                  )}
                </Box>
              )}
              
              {showSuggestions && (
                <Box mt={3}>
                  <Text fontSize="sm" fontWeight="medium" mb={2}>
                    Suggested alternatives:
                  </Text>
                  <HStack wrap="wrap" spacing={2}>
                    {validationResult!.suggestions.map((suggestion, index) => (
                      <Badge
                        key={index}
                        colorScheme="blue"
                        cursor="pointer"
                        onClick={() => setScreenName(suggestion)}
                        _hover={{ bg: 'blue.100' }}
                      >
                        {suggestion}
                      </Badge>
                    ))}
                  </HStack>
                </Box>
              )}
            </FormControl>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button 
            variant="ghost" 
            mr={3} 
            onClick={handleClose}
            isDisabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSubmit}
            isLoading={isSubmitting}
            loadingText={isEdit ? "Updating..." : "Creating..."}
            isDisabled={!isValid || isChecking}
          >
            {isEdit ? 'Update Screen Name' : 'Create Screen Name'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ScreenNameModal;